import { Fragment } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Calculator, ExternalLink, FileSpreadsheet } from 'lucide-react';
import SEO from '@/components/common/SEO';
import SourceBadge from '@/components/financial/SourceBadge';
import {
  cashOnCashRatio,
  getCaseStudyBySlug,
  monthlyProfitCents,
  paybackMonths,
} from '@/content/financial/case-studies';
import { playbooksForCaseStudy } from '@/content/financial/playbooks';
import { CALCULATORS } from '@/content/financial/calculators';
import {
  CATEGORY_LABELS,
  DIFFICULTY_LABELS,
  EFFORT_LABELS,
  SOURCE_LABELS,
} from '@/content/financial/types';
import {
  formatCents,
  formatIsoDate,
  formatMonths,
  formatNumber,
  formatRatioAsPercent,
} from '@/content/financial/format';

const BASE_URL = 'https://koreanhub.net';

export default function CaseStudyDetail() {
  const { slug } = useParams<{ slug: string }>();
  const study = getCaseStudyBySlug(slug);

  if (!study) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-24 text-center">
        <SEO
          title="Case study not found"
          description="This case study does not exist."
          path={`/financial/case-studies/${slug ?? ''}`}
          noindex
        />
        <h1 className="text-2xl font-bold text-ink-900 dark:text-ink-50">Case study not found</h1>
        <p className="mt-3 text-ink-500 dark:text-ink-400">
          It may have been renamed or retired.{' '}
          <Link
            to="/financial/case-studies"
            className="font-medium text-cheong-500 underline dark:text-cheong-dark"
          >
            Browse all case studies
          </Link>
          .
        </p>
      </div>
    );
  }

  const category = CATEGORY_LABELS[study.category];
  const profit = monthlyProfitCents(study);
  const coc = cashOnCashRatio(study);
  const payback = paybackMonths(study);
  const playbooks = playbooksForCaseStudy(study.slug);
  const calculators = CALCULATORS.filter((c) => c.relatedCaseStudySlugs?.includes(study.slug));

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: study.title,
    description: study.dek,
    url: `${BASE_URL}/financial/case-studies/${study.slug}`,
    datePublished: study.publishedAt,
    dateModified: study.verifiedAt,
    author: { '@type': 'Organization', name: 'KoreanHub' },
    publisher: { '@type': 'Organization', name: 'KoreanHub' },
    articleSection: category.label,
    keywords: study.tags.join(', '),
  };

  return (
    <div className="bg-white dark:bg-surface-dark">
      <SEO
        title={study.title}
        description={study.dek}
        path={`/financial/case-studies/${study.slug}`}
        type="article"
        jsonLd={articleSchema}
      />

      {/* ── Hero ── */}
      <header className={`bg-gradient-to-br ${study.gradient}`}>
        <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6 sm:py-16">
          <Link
            to="/financial/case-studies"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-white/70 transition-colors hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Case studies
          </Link>

          <div className="mt-6 flex flex-wrap items-center gap-x-3 gap-y-2">
            <span className="text-[11px] font-bold uppercase tracking-wide text-white/80">
              {category.label}
            </span>
            <span lang="ko" className="font-korean text-xs text-white/60" aria-hidden="true">
              {category.labelKo}
            </span>
          </div>

          <h1 className="mt-3 text-3xl font-bold leading-tight text-white sm:text-4xl">
            {study.title}
          </h1>
          <p className="mt-4 text-lg leading-8 text-white/80">{study.dek}</p>

          <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-sm text-white/70">
            {study.market && <span>{study.market}</span>}
            <span>{EFFORT_LABELS[study.effort]}</span>
            <span>{DIFFICULTY_LABELS[study.difficulty]}</span>
            <span>{study.readingMinutes} min read</span>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6">
        {/* ── Why this is here (diaspora studies only) ── */}
        {study.diasporaAngle && (
          <aside className="mb-10 rounded-xl border-l-4 border-cheong-500 bg-cheong-500/5 p-5 dark:border-cheong-dark">
            <p className="text-[11px] font-bold uppercase tracking-wide text-cheong-500 dark:text-cheong-dark">
              Why this one is here
            </p>
            <p className="mt-2 text-base leading-7 text-ink-700 dark:text-ink-200">
              {study.diasporaAngle}
            </p>
          </aside>
        )}

        {/* ── Economics ── */}
        <section aria-label="Economics" className="mb-10">
          <h2 className="mb-4 text-xl font-bold text-ink-900 dark:text-ink-50">
            The numbers{' '}
            <span lang="ko" className="font-korean text-sm font-medium text-ink-400">
              수치
            </span>
          </h2>
          <dl className="grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-ink-200 bg-ink-200 dark:border-surface-dark-border dark:bg-surface-dark-border sm:grid-cols-3">
            <Metric label="All-in startup cost" value={formatCents(study.startupCostCents)} />
            <Metric
              label="Operator's own cash"
              value={formatCents(study.cashInvestedCents ?? study.startupCostCents)}
              note={
                study.cashInvestedCents != null &&
                study.cashInvestedCents < study.startupCostCents
                  ? 'Rest is debt or seller financing.'
                  : undefined
              }
            />
            <Metric label="Monthly revenue" value={formatCents(study.monthlyRevenueCents)} />
            <Metric
              label="Monthly costs"
              value={formatCents(study.monthlyExpenseCents)}
              note="Includes debt service where the deal carries a note."
            />
            <Metric
              label="Monthly profit"
              value={formatCents(profit)}
              tone={profit >= 0 ? 'good' : 'bad'}
            />
            <Metric
              label="Cash-on-cash"
              value={coc == null ? '—' : formatRatioAsPercent(coc, 1)}
              note={
                coc == null
                  ? 'Capital base too small for the ratio to mean anything — read payback instead.'
                  : 'Annualised, against the operator’s own cash.'
              }
              tone={coc != null && coc > 0 ? 'good' : 'neutral'}
            />
            <Metric
              label="Owner hours"
              value={
                study.hoursPerWeek == null ? '—' : `${formatNumber(study.hoursPerWeek)} hrs/wk`
              }
              note="The honest denominator on “passive income”."
            />
            <Metric label="Payback" value={formatMonths(payback)} />
            <Metric
              label="To break even"
              value={formatMonths(study.monthsToBreakEven)}
              note="From first dollar spent."
            />
          </dl>
        </section>

        {/* ── Provenance ── */}
        <section className="mb-10 rounded-xl border border-ink-200 bg-ink-50 p-5 dark:border-surface-dark-border dark:bg-white/[0.03]">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <h2 className="text-sm font-bold text-ink-900 dark:text-ink-50">
              Where these numbers came from
            </h2>
            <SourceBadge sourceType={study.sourceType} linked />
          </div>
          <p className="mt-2 text-sm leading-relaxed text-ink-700 dark:text-ink-200">
            <strong className="font-semibold">{SOURCE_LABELS[study.sourceType]}.</strong>{' '}
            {study.sourceNote}
          </p>
          <p className="mt-2 text-xs text-ink-500 dark:text-ink-400">
            Economics last checked {formatIsoDate(study.verifiedAt)}.{' '}
            <Link
              to="/financial/methodology"
              className="text-cheong-500 underline dark:text-cheong-dark"
            >
              What this sourcing tier can and cannot tell you
            </Link>
            .
          </p>
        </section>

        {/* ── Body ── */}
        <section className="mb-12">
          <Prose text={study.body} />
        </section>

        {/* ── Execution ── */}
        <section className="mb-12">
          <h2 className="mb-5 text-xl font-bold text-ink-900 dark:text-ink-50">
            How it was done{' '}
            <span lang="ko" className="font-korean text-sm font-medium text-ink-400">
              실행 순서
            </span>
          </h2>
          <ol className="space-y-4">
            {study.playbookSteps.map((step, i) => (
              <li
                key={step.title}
                className="rounded-xl border border-ink-200 bg-white p-5 shadow-sm dark:border-surface-dark-border dark:bg-surface-dark-card"
              >
                <div className="flex items-start gap-4">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-cheong-500 font-mono text-sm font-bold text-white">
                    {i + 1}
                  </span>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                      <h3 className="text-base font-bold text-ink-900 dark:text-ink-50">
                        {step.title}
                      </h3>
                      {step.durationLabel && (
                        <span className="rounded-full bg-ink-100 px-2.5 py-0.5 text-[11px] font-medium text-ink-500 dark:bg-white/10 dark:text-ink-400">
                          {step.durationLabel}
                        </span>
                      )}
                    </div>
                    <p className="mt-2 text-sm leading-6 text-ink-700 dark:text-ink-200">
                      {step.detail}
                    </p>
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </section>

        {/* ── Risks ── */}
        <section className="mb-12">
          <h2 className="mb-4 flex items-center gap-2 text-xl font-bold text-ink-900 dark:text-ink-50">
            <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-500" aria-hidden="true" />
            What goes wrong{' '}
            <span lang="ko" className="font-korean text-sm font-medium text-ink-400">
              위험 요소
            </span>
          </h2>
          <ul className="space-y-3 rounded-xl border border-amber-200 bg-amber-50 p-5 dark:border-amber-900/40 dark:bg-amber-900/10">
            {study.risks.map((risk) => (
              <li
                key={risk}
                className="flex gap-3 text-sm leading-6 text-amber-900 dark:text-amber-200"
              >
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-600" aria-hidden="true" />
                <span>{risk}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* ── Tech stack ── */}
        <section className="mb-12">
          <h2 className="mb-4 text-xl font-bold text-ink-900 dark:text-ink-50">
            What it runs on{' '}
            <span lang="ko" className="font-korean text-sm font-medium text-ink-400">
              사용 도구
            </span>
          </h2>
          <ul className="flex flex-wrap gap-2">
            {study.techStack.map((tool) => (
              <li
                key={tool}
                className="rounded-full bg-ink-100 px-3 py-1.5 text-sm text-ink-700 dark:bg-white/10 dark:text-ink-200"
              >
                {tool}
              </li>
            ))}
          </ul>
        </section>

        {/* ── Sources ── */}
        {study.sources && study.sources.length > 0 && (
          <section className="mb-12">
            <h2 className="mb-4 text-xl font-bold text-ink-900 dark:text-ink-50">
              Primary sources{' '}
              <span lang="ko" className="font-korean text-sm font-medium text-ink-400">
                출처
              </span>
            </h2>
            <ul className="space-y-3">
              {study.sources.map((source) => (
                <li key={source.url} className="text-sm leading-6">
                  <a
                    href={source.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-start gap-1.5 font-medium text-cheong-500 hover:underline dark:text-cheong-dark"
                  >
                    {source.label}
                    <ExternalLink className="mt-1 h-3 w-3 shrink-0" aria-hidden="true" />
                  </a>
                  <span className="block text-ink-500 dark:text-ink-400">{source.publisher}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* ── Cross-links ── */}
        {(calculators.length > 0 || playbooks.length > 0) && (
          <section className="mb-12 border-t border-ink-200 pt-8 dark:border-surface-dark-border">
            <h2 className="mb-4 text-xl font-bold text-ink-900 dark:text-ink-50">
              Run the numbers yourself
            </h2>
            <div className="grid gap-4 sm:grid-cols-2">
              {calculators.map((calc) => (
                <Link
                  key={calc.slug}
                  to={`/financial/calculators/${calc.slug}`}
                  className="flex items-start gap-3 rounded-xl border border-ink-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md dark:border-surface-dark-border dark:bg-surface-dark-card"
                >
                  <Calculator
                    className="mt-0.5 h-4 w-4 shrink-0 text-cheong-500 dark:text-cheong-dark"
                    aria-hidden="true"
                  />
                  <span>
                    <span className="block text-sm font-bold text-ink-900 dark:text-ink-50">
                      {calc.name}
                    </span>
                    <span className="mt-1 block text-xs leading-5 text-ink-500 dark:text-ink-400">
                      {calc.summary}
                    </span>
                  </span>
                </Link>
              ))}
              {playbooks.map((pb) => (
                <Link
                  key={pb.slug}
                  to="/financial/playbooks"
                  className="flex items-start gap-3 rounded-xl border border-ink-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md dark:border-surface-dark-border dark:bg-surface-dark-card"
                >
                  <FileSpreadsheet
                    className="mt-0.5 h-4 w-4 shrink-0 text-hwang-600 dark:text-hwang-500"
                    aria-hidden="true"
                  />
                  <span>
                    <span className="block text-sm font-bold text-ink-900 dark:text-ink-50">
                      {pb.title}
                    </span>
                    <span className="mt-1 block text-xs leading-5 text-ink-500 dark:text-ink-400">
                      {pb.format} · {pb.pages} pages
                    </span>
                  </span>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* ── Disclaimer ── */}
        <p className="border-t border-ink-200 pt-6 text-xs leading-relaxed text-ink-500 dark:border-surface-dark-border dark:text-ink-400">
          Educational analysis, not investment, tax, or legal advice. Outcomes in small business
          vary enormously, and most attempts at most business models do not reach the returns shown
          in any published case study.{' '}
          <Link
            to="/financial/disclosures"
            className="text-cheong-500 underline dark:text-cheong-dark"
          >
            Full disclosures
          </Link>
          .
        </p>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

function Metric({
  label,
  value,
  note,
  tone = 'neutral',
}: {
  label: string;
  value: string;
  note?: string;
  tone?: 'neutral' | 'good' | 'bad';
}) {
  const toneClass =
    tone === 'good'
      ? 'text-green-600 dark:text-green-500'
      : tone === 'bad'
        ? 'text-red-600 dark:text-red-400'
        : 'text-ink-900 dark:text-ink-50';

  return (
    <div className="bg-white p-4 dark:bg-surface-dark-card">
      <dt className="text-[10px] font-bold uppercase tracking-wide text-ink-400">{label}</dt>
      <dd className={`mt-1 font-mono text-lg font-semibold tabular-nums ${toneClass}`}>{value}</dd>
      {note && <dd className="mt-1 text-[11px] leading-4 text-ink-400">{note}</dd>}
    </div>
  );
}

/**
 * Blank-line-separated paragraphs with `**bold**` spans.
 *
 * Deliberately not a markdown library: the case-study bodies use exactly two
 * features, and pulling a parser in would add a dependency and an XSS surface
 * to render emphasis on three words.
 */
function Prose({ text }: { text: string }) {
  return (
    <div className="space-y-5">
      {text.split(/\n\n+/).map((para) => (
        <p key={para} className="text-base leading-8 text-ink-700 dark:text-ink-200">
          {para.split(/(\*\*[^*]+\*\*)/g).map((chunk, i) =>
            chunk.startsWith('**') && chunk.endsWith('**') ? (
              <strong key={i} className="font-semibold text-ink-900 dark:text-ink-50">
                {chunk.slice(2, -2)}
              </strong>
            ) : (
              <Fragment key={i}>{chunk}</Fragment>
            )
          )}
        </p>
      ))}
    </div>
  );
}
